import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Word Pyramids — FTUE demo (3 essais).
 *
 * E1 : 4 lettres ROSE — pédagogie de base "tap les lettres puis valider"
 * E2 : 5 lettres TABLE — montre qu'on peut trouver plusieurs mots
 * E3 : Calibration normale (skip vers session)
 */
import { useMemo, useState } from 'react';
import { isAccepted, normalizeWord } from './data';
const DEMO_PUZZLES = [
    {
        idx: 1,
        title: 'Essai 1 — 4 lettres',
        hint: 'Tap les lettres pour composer un mot, puis Valider. Cherche 1 mot.',
        puzzle: {
            id: 'demo-1',
            letters: 'ROSE',
            accepted: ['ROSE', 'ORS', 'EROS', 'OSE'],
        },
        required: 1,
    },
    {
        idx: 2,
        title: 'Essai 2 — 5 lettres',
        hint: "Plusieurs mots possibles. Trouve-en 2.",
        puzzle: {
            id: 'demo-2',
            letters: 'TABLE',
            accepted: ['TABLE', 'BAL', 'BLE', 'TEL', 'ALE', 'BAT', 'ABLE'],
        },
        required: 2,
    },
    {
        idx: 3,
        title: 'Essai 3 — prêt',
        hint: 'Tu es prêt. Le round normal dure 60 à 90 secondes.',
        puzzle: {
            id: 'demo-3',
            letters: 'CARTE',
            accepted: ['CARTE', 'ACTE', 'CAR', 'RAT', 'ART', 'TARE', 'TRACE'],
        },
        required: 1,
    },
];
export function Demo({ onComplete, onSkip }) {
    const [stepIdx, setStepIdx] = useState(0);
    const [composing, setComposing] = useState([]);
    const [accepted, setAccepted] = useState([]);
    const step = DEMO_PUZZLES[stepIdx];
    const isLast = stepIdx === DEMO_PUZZLES.length - 1;
    const letters = step.puzzle.letters.split('');
    const composedWord = useMemo(() => composing.map((i) => letters[i] ?? '').join(''), [composing, letters]);
    const enough = accepted.length >= step.required;
    const handleTap = (i) => {
        if (composing.includes(i))
            return;
        setComposing((c) => [...c, i]);
    };
    const handleClear = () => setComposing([]);
    const handleSubmit = () => {
        const word = normalizeWord(composedWord);
        if (word.length < 3)
            return;
        if (accepted.includes(word))
            return;
        if (!isAccepted(word, step.puzzle))
            return;
        setAccepted((a) => [...a, word]);
        setComposing([]);
    };
    const handleNext = () => {
        if (isLast) {
            onComplete();
            return;
        }
        setStepIdx((i) => i + 1);
        setComposing([]);
        setAccepted([]);
    };
    return (_jsxs("div", { className: "flex flex-1 flex-col items-center gap-s4 py-s4 text-center", children: [_jsx("p", { className: "font-mono text-[9.5px] uppercase tracking-[0.30em] text-cyan-text-faint", children: "D\u00E9mo guid\u00E9e" }), _jsx("h2", { className: "font-display text-[22px] font-medium leading-7 tracking-cy-display text-cyan-text", children: step.title }), _jsx("p", { className: "max-w-[320px] text-[13.5px] text-cyan-text-secondary", children: step.hint }), _jsx("div", { className: "relative flex h-[64px] w-full max-w-[320px] items-center justify-center bg-cyan-network", children: _jsx("span", { className: "font-display text-[28px] tracking-[0.16em] text-cyan-surface", children: composedWord || '—' }) }), _jsx("div", { className: "flex flex-wrap justify-center gap-s2 max-w-[320px]", children: letters.map((l, i) => {
                    const used = composing.includes(i);
                    return (_jsx("button", { type: "button", disabled: used, onClick: () => handleTap(i), className: [
                            'flex h-[52px] w-[44px] items-center justify-center text-[20px] font-display',
                            used
                                ? 'bg-cyan-border-faint text-cyan-text-secondary opacity-40'
                                : 'border border-cyan-border bg-cyan-surface text-cyan-text hover:scale-[1.04] active:scale-95',
                        ].join(' '), children: l }, `${l}-${i}`));
                }) }), _jsxs("div", { className: "flex justify-center gap-s3", children: [_jsx("button", { type: "button", onClick: handleClear, className: "border border-cyan-border-faint px-s4 py-s2 text-[13px] text-cyan-text-secondary hover:border-cyan-border", children: "Effacer" }), _jsx("button", { type: "button", onClick: handleSubmit, disabled: composedWord.length < 3, className: "bg-cyan-core px-s5 py-s2 font-display text-[13px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network disabled:opacity-40", children: "Valider" })] }), accepted.length > 0 && (_jsx("div", { className: "flex flex-wrap justify-center gap-s2", children: accepted.map((w) => (_jsx("span", { className: "border border-cyan-border-faint bg-cyan-surface px-s3 py-s1 text-[11px] text-cyan-text", children: w }, w))) })), _jsxs("div", { className: "flex flex-col items-stretch gap-s3 self-stretch mt-auto", children: [enough && (_jsx("button", { type: "button", onClick: handleNext, className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: isLast ? 'Démarrer la session' : 'Essai suivant' })), _jsx("button", { type: "button", onClick: onSkip, className: "text-[12px] text-cyan-text-secondary underline-offset-4 hover:underline", children: "Passer la d\u00E9mo" })] })] }));
}
//# sourceMappingURL=demo.js.map